import { useState } from "react";
import { Menu, X } from "lucide-react";
import { AppLink } from "@/shared/components/AppLink";
import { Container } from "@/shared/components/Container";
import { Button } from "@/shared/components/Button";
import { useSiteSettings } from "@/features/content/hooks/useSiteSettings";
import { useLocale } from "@/shared/i18n/locale";
import { getHomeContent } from "@/content/home.content";
import { LanguageSelector } from "@/shared/components/LanguageSelector";
import styles from "./Header.module.css";
import type { HeaderProps, NavItem } from "./Header.types";

export function Header({ variant = "dark", compact = false }: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { articlesEnabled } = useSiteSettings();
  const locale = useLocale();
  const content = getHomeContent(locale);

  const prefix = locale === "en" ? "/en" : "";
  const articlesHref = `${prefix}/#artigos`;
  const contactFormHref = `${prefix}/#contato-formulario`;

  const navItemsList: NavItem[] = [
    { label: content.nav.results, href: `${prefix}/#resultados` },
    { label: content.nav.cases, href: `${prefix}/#cases` },
    { label: content.nav.method, href: `${prefix}/#metodo` },
    { label: content.nav.education, href: `${prefix}/#educacao` },
    { label: content.nav.articles, href: articlesHref },
  ];

  // Esconde o link de Artigos quando o conteúdo está desativado no CMS.
  const navItems = articlesEnabled
    ? navItemsList
    : navItemsList.filter((item) => item.href !== articlesHref);

  const logoSrc =
    variant === "light" ? "/assets/logo-autou-orange.svg" : "/assets/logo-autou-white.svg";
  const headerClassName = [
    styles.header,
    variant === "light" && styles.light,
    compact && styles.compact,
    isMobileMenuOpen && styles.menuOpen,
  ]
    .filter(Boolean)
    .join(" ");

  const mobileMenuId = "header-mobile-menu";
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <header className={headerClassName}>
      <Container>
        <div className={styles.inner}>
          <AppLink href={locale === "en" ? "/en" : "/"} aria-label={content.nav.homeAriaLabel}>
            <img
              className={styles.logo}
              src={logoSrc}
              alt="AutoU"
              width={162}
              height={41}
              loading="eager"
              decoding="async"
            />
          </AppLink>

          <nav className={styles.nav} aria-label={locale === "en" ? "Main navigation" : "Navegação principal"}>
            {navItems.map((item) => (
              <AppLink key={item.href} href={item.href} className={styles.navLink}>
                {item.label}
              </AppLink>
            ))}
          </nav>

          <div className={styles.ctaGroup}>
            <LanguageSelector variant={variant} compact={compact} />
            <div className={styles.cta}>
              <Button href={contactFormHref} variant="primary">
                {content.nav.scheduleMeeting}
              </Button>
            </div>
          </div>

          <button
            className={styles.menuToggle}
            type="button"
            aria-label={isMobileMenuOpen ? content.nav.closeMenu : content.nav.openMenu}
            aria-controls={mobileMenuId}
            aria-expanded={isMobileMenuOpen}
            onClick={() => setIsMobileMenuOpen((currentValue) => !currentValue)}
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        <nav
          id={mobileMenuId}
          className={styles.mobileMenu}
          aria-label={locale === "en" ? "Mobile main navigation" : "Navegação principal mobile"}
        >
          {navItems.map((item) => (
            <AppLink
              key={item.href}
              href={item.href}
              className={styles.mobileNavLink}
              onClick={closeMobileMenu}
            >
              {item.label}
            </AppLink>
          ))}
          <LanguageSelector mobile onSelect={closeMobileMenu} />
          <AppLink
            href={contactFormHref}
            className={styles.mobileCtaLink}
            onClick={closeMobileMenu}
          >
            {content.nav.scheduleMeeting}
          </AppLink>
        </nav>
      </Container>
    </header>
  );
}
